import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getCAPAs, createCAPA, updateCAPA, getUsers } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, Search, Shield, ChevronLeft, AlertTriangle, Eye, Edit, 
  Calendar, User, CheckCircle, Clock, MessageSquare, Target,
  FileText, Activity, Plane
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import HelpTooltip from '@/components/help/HelpTooltip';
import PolishButton from '@/components/PolishButton';
import CollaborationPanel from '@/components/CollaborationPanel';
import AssetSelector, { AssetDisplay } from '@/components/AssetSelector';

const STATUS_COLORS = {
  open: 'bg-blue-900/30 text-blue-400 border-blue-900/50',
  in_progress: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50',
  verification: 'bg-violet-900/30 text-violet-400 border-violet-900/50',
  closed: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50',
  overdue: 'bg-red-900/30 text-red-400 border-red-900/50',
};

const STATUS_STEPS = ['open', 'in_progress', 'verification', 'closed'];

export default function CAPAsPage({ initialMode }) {
  const { t } = useTranslation();
  const { api, user, token } = useAuth();
  const [capas, setCAPAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(initialMode || 'list');
  const [selectedCAPA, setSelectedCAPA] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [users, setUsers] = useState([]);
  const [showCollaboration, setShowCollaboration] = useState(true);

  const [formData, setFormData] = useState({
    title: '', description: '', capa_type: 'corrective', source: 'incident',
    due_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    assigned_to: '', root_cause: '', action_plan: '', verification_method: '',
    // Asset linking fields
    aircraft_id: null, aircraft_registration: null, engine_id: null, component_id: null,
  });

  useEffect(() => {
    if (mode === 'list') fetchCAPAs();
    fetchUsers();
  }, [mode]);

  const fetchCAPAs = async () => {
    setLoading(true);
    try { 
      const response = await getCAPAs(api); 
      setCAPAs(response.data); 
    }
    catch (error) { console.error('Failed to fetch CAPAs:', error); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try { const response = await getUsers(api); setUsers(response.data); }
    catch (error) { console.error('Failed to fetch users:', error); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createCAPA(api, { ...formData, due_date: new Date(formData.due_date).toISOString() });
      toast.success('CAPA created successfully');
      setMode('list');
      resetForm();
    } catch (error) { 
      console.error('Failed to create CAPA:', error); 
      toast.error('Failed to create CAPA');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateCAPA(api, selectedCAPA.id, { ...formData, due_date: new Date(formData.due_date).toISOString() });
      toast.success('CAPA updated successfully');
      setMode('list');
      setSelectedCAPA(null);
      resetForm();
    } catch (error) { 
      console.error('Failed to update CAPA:', error); 
      toast.error('Failed to update CAPA');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateCAPA(api, selectedCAPA.id, { status: newStatus });
      setSelectedCAPA({ ...selectedCAPA, status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', capa_type: 'corrective', source: 'incident',
      due_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      assigned_to: '', root_cause: '', action_plan: '', verification_method: '',
      aircraft_id: null, aircraft_registration: null, engine_id: null, component_id: null,
    });
  };

  const handleView = (capa) => {
    setSelectedCAPA(capa);
    setMode('view');
  };

  const handleEdit = (capa) => {
    setSelectedCAPA(capa);
    setFormData({
      title: capa.title || '',
      description: capa.description || '',
      capa_type: capa.capa_type || 'corrective',
      source: capa.source || 'incident',
      due_date: capa.due_date ? new Date(capa.due_date).toISOString().split('T')[0] : '',
      assigned_to: capa.assigned_to || '',
      root_cause: capa.root_cause || '',
      action_plan: capa.action_plan || '',
      verification_method: capa.verification_method || '',
      aircraft_id: capa.aircraft_id || null,
      aircraft_registration: capa.aircraft_registration || null,
      engine_id: capa.engine_id || null,
      component_id: capa.component_id || null,
    });
    setMode('edit');
  };

  const isOverdue = (capa) => {
    return new Date(capa.due_date) < new Date() && !['closed', 'verification'].includes(capa.status);
  };

  const getProgressPercentage = (status) => {
    const index = STATUS_STEPS.indexOf(status);
    return ((index + 1) / STATUS_STEPS.length) * 100;
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser ? foundUser.name : 'Unassigned';
  };

  const filteredCAPAs = capas.filter(c => {
    const matchesSearch = c.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          c.capa_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus || 
                          (filterStatus === 'overdue' && isOverdue(c));
    return matchesSearch && matchesStatus;
  });

  // Statistics
  const stats = {
    total: capas.length,
    open: capas.filter(c => c.status === 'open').length,
    inProgress: capas.filter(c => c.status === 'in_progress').length,
    verification: capas.filter(c => c.status === 'verification').length,
    closed: capas.filter(c => c.status === 'closed').length,
    overdue: capas.filter(c => isOverdue(c)).length,
  };

  // View Mode
  if (mode === 'view' && selectedCAPA) {
    const capa = selectedCAPA;
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setMode('list'); setSelectedCAPA(null); }} className="text-slate-400 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white font-barlow">{capa.capa_number}</h1>
                <Badge className={`${isOverdue(capa) ? STATUS_COLORS.overdue : STATUS_COLORS[capa.status]} border`}>
                  {isOverdue(capa) ? 'Overdue' : capa.status?.replace('_', ' ')}
                </Badge>
                <Badge variant="outline" className="border-slate-600 text-slate-400 capitalize">
                  {capa.capa_type}
                </Badge>
              </div>
              <p className="text-slate-400 mt-1">{capa.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleEdit(capa)} className="bg-sky-600 hover:bg-sky-700">
              <Edit className="w-4 h-4 mr-2" />Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCollaboration(!showCollaboration)}
              className={`border-slate-700 ${showCollaboration ? 'bg-cyan-600/20 border-cyan-600/50 text-cyan-400' : 'text-slate-300'}`}
              data-testid="toggle-collaboration-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />Collaborate
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Progress</span>
              <span className="text-sm text-white">{Math.round(getProgressPercentage(capa.status))}%</span>
            </div>
            <Progress value={getProgressPercentage(capa.status)} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              {STATUS_STEPS.map((step, idx) => (
                <span key={step} className={STATUS_STEPS.indexOf(capa.status) >= idx ? 'text-cyan-400' : ''}>
                  {step.replace('_', ' ')}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 whitespace-pre-wrap">{capa.description || 'No description provided'}</p>
              </CardContent>
            </Card>

            {/* Root Cause & Action Plan */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-400" />
                  Root Cause Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-500 text-sm">Root Cause</Label>
                  <p className="text-slate-300 mt-1">{capa.root_cause || 'Not yet identified'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Action Plan</Label>
                  <p className="text-slate-300 mt-1 whitespace-pre-wrap">{capa.action_plan || 'No action plan defined'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Verification Method</Label>
                  <p className="text-slate-300 mt-1">{capa.verification_method || 'Not specified'}</p>
                </div>
                
                {/* Linked Assets */}
                {(capa.aircraft_id || capa.engine_id || capa.component_id) && (
                  <div className="pt-4 border-t border-slate-800">
                    <Label className="text-slate-500 text-sm flex items-center gap-2">
                      <Plane className="w-4 h-4" />
                      Linked Assets
                    </Label>
                    <div className="mt-2">
                      <AssetDisplay
                        aircraftId={capa.aircraft_id}
                        aircraftRegistration={capa.aircraft_registration}
                        engineId={capa.engine_id}
                        engineSerial={capa.engine_serial}
                        componentId={capa.component_id}
                        componentPartNumber={capa.component_part_number}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Actions */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  Status Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {capa.status === 'open' && (
                    <Button onClick={() => handleStatusChange('in_progress')} className="bg-yellow-600 hover:bg-yellow-700">
                      Start Work
                    </Button>
                  )}
                  {capa.status === 'in_progress' && (
                    <Button onClick={() => handleStatusChange('verification')} className="bg-violet-600 hover:bg-violet-700">
                      Submit for Verification
                    </Button>
                  )}
                  {capa.status === 'verification' && (
                    <>
                      <Button onClick={() => handleStatusChange('closed')} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle className="w-4 h-4 mr-2" />Verify & Close
                      </Button>
                      <Button onClick={() => handleStatusChange('in_progress')} variant="outline" className="border-yellow-600 text-yellow-400">
                        Return for Rework
                      </Button>
                    </>
                  )}
                  {capa.status === 'closed' && (
                    <Badge className="bg-emerald-900/30 text-emerald-400 border border-emerald-900/50 py-2 px-4">
                      <CheckCircle className="w-4 h-4 mr-2" />Closed & Verified
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Details Card */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Assigned To</p>
                    <p className="text-white">{getUserName(capa.assigned_to)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className={`w-4 h-4 ${isOverdue(capa) ? 'text-red-400' : 'text-slate-500'}`} />
                  <div>
                    <p className="text-xs text-slate-500">Due Date</p>
                    <p className={isOverdue(capa) ? 'text-red-400' : 'text-white'}>
                      {format(new Date(capa.due_date), 'PPP')}
                      {isOverdue(capa) && <span className="ml-2 text-xs">(Overdue)</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Source</p>
                    <p className="text-white capitalize">{capa.source?.replace('_', ' ')}</p>
                  </div>
                </div>
                {capa.source_id && (
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <div>
                      <p className="text-xs text-slate-500">Source Reference</p>
                      <p className="text-cyan-400 font-mono text-sm">{capa.source_id}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-white">{format(new Date(capa.created_at), 'PPP')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Closure Validation (if applicable) */}
            {capa.closure_validation_checklist && capa.closure_validation_checklist.length > 0 && (
              <Card className="bg-slate-900/60 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    Closure Checklist
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {capa.closure_validation_checklist.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className={`w-4 h-4 ${item.completed ? 'text-emerald-400' : 'text-slate-600'}`} />
                        <span className={item.completed ? 'text-slate-300' : 'text-slate-500'}>{item.item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Collaboration Panel */}
        {showCollaboration && (
          <CollaborationPanel
            entityType="capa"
            entityId={capa.id}
            entityTitle={capa.title}
            apiClient={api}
            currentUser={user}
            token={token}
            isOpen={showCollaboration}
            onClose={() => setShowCollaboration(false)}
          />
        )}
      </div>
    );
  }

  // Create/Edit Form
  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => { setMode('list'); setSelectedCAPA(null); resetForm(); }} className="text-slate-400 hover:text-white">
            <ChevronLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <h1 className="text-2xl font-bold text-white font-barlow">
            {mode === 'create' ? t('capas.newCapa') : `Edit ${selectedCAPA?.capa_number}`}
          </h1>
        </div>
        <Card className="bg-slate-900/60 border-slate-800 max-w-4xl">
          <CardContent className="p-6">
            <form onSubmit={mode === 'create' ? handleCreate : handleUpdate} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Basic Information</h3>
                <div>
                  <Label className="text-slate-300">Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" data-testid="capa-title-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">{t('incidents.description')} *</Label>
                    <PolishButton
                      text={formData.description}
                      kind="capa"
                      token={token}
                      onApply={(polished) => setFormData({ ...formData, description: polished })}
                    />
                  </div>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required rows={3} className="bg-slate-950 border-slate-800 mt-1" data-testid="capa-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 inline-flex items-center">
                      {t('capas.capaType')} *
                      <HelpTooltip
                        label="CAPA Type"
                        body={"• Corrective — you're fixing a problem that has already happened (e.g. after an incident).\n\n• Preventive — you're stopping a problem before it happens (e.g. after spotting a hazard in an audit).\n\nWhen in doubt, pick Corrective."}
                        articleId="task-close-capa"
                      />
                    </Label>
                    <Select value={formData.capa_type} onValueChange={(v) => setFormData({...formData, capa_type: v})}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="corrective">{t('capas.types.corrective')}</SelectItem>
                        <SelectItem value="preventive">{t('capas.types.preventive')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Source</Label>
                    <Select value={formData.source} onValueChange={(v) => setFormData({...formData, source: v})}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="incident">Incident</SelectItem>
                        <SelectItem value="audit">Audit Finding</SelectItem>
                        <SelectItem value="risk">Risk Assessment</SelectItem>
                        <SelectItem value="inspection">Inspection</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">{t('capas.dueDate')} *</Label>
                    <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} required className="bg-slate-950 border-slate-800 mt-1" data-testid="capa-date-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300">{t('capas.assignedTo')} *</Label>
                    <Select value={formData.assigned_to} onValueChange={(v) => setFormData({...formData, assigned_to: v})}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 mt-1" data-testid="capa-assigned-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Root Cause & Action Plan */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">Analysis & Action</h3>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300 inline-flex items-center">
                      Root Cause
                      <HelpTooltip
                        label="Root Cause"
                        body={"The DEEPEST reason the problem happened — not just what happened.\n\nAsk 'why?' five times.\nExample: A pilot forgot a checklist item →\n• Why? Distracted by a radio call\n• Why? ATC frequency change came late\n• Why? Handover procedure not clear\n• Why? SOP not updated\n• ROOT CAUSE: SOP gap for late-frequency handovers.\n\nFix the root cause, not the symptom."}
                        articleId="task-close-capa"
                        example="SOP gap for late frequency handovers"
                        onApplyExample={(v) => setFormData({...formData, root_cause: v})}
                      />
                    </Label>
                    <PolishButton
                      text={formData.root_cause}
                      kind="capa"
                      token={token}
                      onApply={(polished) => setFormData({ ...formData, root_cause: polished })}
                    />
                  </div>
                  <Textarea 
                    value={formData.root_cause} 
                    onChange={(e) => setFormData({...formData, root_cause: e.target.value})} 
                    rows={2} 
                    className="bg-slate-950 border-slate-800 mt-1" 
                    placeholder="Identify the root cause of the issue..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Action Plan</Label>
                  <Textarea 
                    value={formData.action_plan} 
                    onChange={(e) => setFormData({...formData, action_plan: e.target.value})} 
                    rows={3} 
                    className="bg-slate-950 border-slate-800 mt-1" 
                    placeholder="Describe the corrective/preventive actions to be taken..."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Verification Method</Label>
                  <Input 
                    value={formData.verification_method} 
                    onChange={(e) => setFormData({...formData, verification_method: e.target.value})} 
                    className="bg-slate-950 border-slate-800 mt-1" 
                    placeholder="How will completion be verified?"
                  />
                </div>

                {/* Asset Selector - Link to Aircraft/Engine/Component */}
                <div className="md:col-span-2">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <AssetSelector
                        value={{
                          aircraft_id: formData.aircraft_id,
                          aircraft_registration: formData.aircraft_registration,
                          engine_id: formData.engine_id,
                          component_id: formData.component_id,
                        }}
                        onChange={(assetData) => {
                          setFormData({
                            ...formData,
                            aircraft_id: assetData.aircraft_id,
                            aircraft_registration: assetData.aircraft_registration,
                            engine_id: assetData.engine_id,
                            component_id: assetData.component_id,
                          });
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-capa-btn">
                  {mode === 'create' ? t('common.create') : 'Save Changes'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setMode('list'); setSelectedCAPA(null); resetForm(); }} className="border-slate-700 text-slate-300">
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // List Mode
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="capas-title">{t('capas.title')}</h1>
          <p className="text-slate-400 mt-1">Track corrective and preventive actions</p>
        </div>
        <Button onClick={() => setMode('create')} className="bg-sky-600 hover:bg-sky-700" data-testid="new-capa-btn">
          <Plus className="w-4 h-4 mr-2" />{t('capas.newCapa')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-slate-400">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{stats.open}</p>
            <p className="text-xs text-slate-400">Open</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-400">{stats.inProgress}</p>
            <p className="text-xs text-slate-400">In Progress</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-violet-400">{stats.verification}</p>
            <p className="text-xs text-slate-400">Verification</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats.closed}</p>
            <p className="text-xs text-slate-400">Closed</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800 border-red-900/50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{stats.overdue}</p>
            <p className="text-xs text-red-400">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input placeholder="Search CAPAs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-950 border-slate-800" data-testid="capa-search" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="verification">Verification</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner w-8 h-8" /></div>
          ) : filteredCAPAs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('common.noData')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">{t('capas.capaNumber')}</TableHead>
                  <TableHead className="text-slate-400">Title</TableHead>
                  <TableHead className="text-slate-400">{t('capas.capaType')}</TableHead>
                  <TableHead className="text-slate-400">{t('common.status')}</TableHead>
                  <TableHead className="text-slate-400">{t('capas.assignedTo')}</TableHead>
                  <TableHead className="text-slate-400">{t('capas.dueDate')}</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCAPAs.map((capa) => (
                  <TableRow key={capa.id} className="border-slate-800 hover:bg-slate-800/50">
                    <TableCell className="font-mono text-sky-400">{capa.capa_number}</TableCell>
                    <TableCell className="text-white max-w-[200px] truncate">{capa.title}</TableCell>
                    <TableCell className="text-slate-300 capitalize">{capa.capa_type}</TableCell>
                    <TableCell>
                      <Badge className={`${isOverdue(capa) ? STATUS_COLORS.overdue : STATUS_COLORS[capa.status]} border`}>
                        {isOverdue(capa) ? t('capas.statuses.overdue') : t(`capas.statuses.${capa.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">{getUserName(capa.assigned_to)}</TableCell>
                    <TableCell className={`${isOverdue(capa) ? 'text-red-400' : 'text-slate-400'}`}>
                      <div className="flex items-center gap-1">
                        {isOverdue(capa) && <AlertTriangle className="w-4 h-4" />}
                        {format(new Date(capa.due_date), 'yyyy-MM-dd')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleView(capa)} className="text-slate-400 hover:text-white" data-testid={`view-capa-${capa.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(capa)} className="text-slate-400 hover:text-white" data-testid={`edit-capa-${capa.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
